import { PAGES } from "@dune/types";
import {
	Button,
	Card,
	CardContent,
	Input,
	Label,
	RadioGroup,
	RadioGroupItem,
	Separator,
} from "@dune/ui";
import { Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToggleSkipConstructionSite } from "../../../../api/mutations/constructionSite.mutations";
import { useUpdateConstructionSiteValidation } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { useGetSupportingDocumentsAnalysis } from "../../../../api/queries/constructionSiteAnalysis.queries";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";

interface ValidationStepProps {
	handlePreviousStep: () => void;
}

export const ValidationStep = ({ handlePreviousStep }: ValidationStepProps) => {
	const navigate = useNavigate();
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const [validated, setValidated] = useState<"yes" | "no">("yes");
	const [reserves, setReserves] = useState<Array<{ id: string; text: string }>>(
		[],
	);
	const [addReservesEnabled, setAddReservesEnabled] = useState<"yes" | "no">(
		"yes",
	);
	const [skipChoice, setSkipChoice] = useState<"yes" | "no">("no");
	const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
	const [lastAddedId, setLastAddedId] = useState<string | null>(null);

	const { data: supportingDocumentsData } = useGetSupportingDocumentsAnalysis(
		Number(constructionSiteId),
	);

	const defaultReserves = useMemo(() => {
		const reserves: Array<{ id: string; text: string }> = [];

		if (supportingDocumentsData?.missingDocuments) {
			for (const doc of supportingDocumentsData.missingDocuments) {
				if (doc.comment) {
					reserves.push({
						id: `${Date.now()}-missing-${doc.documentType}`,
						text: doc.comment,
					});
				}
			}
		}

		if (supportingDocumentsData?.nonCompliantDocuments) {
			for (const doc of supportingDocumentsData.nonCompliantDocuments) {
				if (doc.comment) {
					reserves.push({
						id: `${Date.now()}-noncompliant-${doc.documentType}`,
						text: doc.comment,
					});
				}
			}
		}

		return reserves;
	}, [supportingDocumentsData]);

	useEffect(() => {
		if (
			validated === "yes" &&
			addReservesEnabled === "yes" &&
			reserves.length === 0
		) {
			setReserves(defaultReserves);
		}
	}, [validated, addReservesEnabled, reserves.length, defaultReserves]);

	const toggleSkipMutation = useToggleSkipConstructionSite();

	const saveValidation = useUpdateConstructionSiteValidation(
		Number(constructionSiteId),
	);

	const handleAddReserve = () => {
		const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
		setReserves((prev) => [...prev, { id, text: "" }]);
		setLastAddedId(id);
	};

	const handleRemoveReserve = (index: number) => {
		setReserves((prev) => prev.filter((_, i) => i !== index));
	};

	const handleReserveChange = (index: number, value: string) => {
		setReserves((prev) =>
			prev.map((r, i) => (i === index ? { ...r, text: value } : r)),
		);
	};

	useEffect(() => {
		if (!lastAddedId) return;
		const el = inputRefs.current[lastAddedId];
		if (el) el.focus();
		setLastAddedId(null);
	}, [lastAddedId]);

	const handleFinalize = async () => {
		if (validated === "no") {
			return;
		}

		saveValidation.mutate(
			{
				validated: validated === "yes",
				addReservesEnabled: addReservesEnabled === "yes",
				reserves: reserves
					.map((r) => r.text)
					.filter((t) => t.trim().length > 0),
			},
			{
				onSuccess: () => {
					navigate(
						`${PAGES.PROJECTS.show.withParams(constructionSiteId)}?tab=taches`,
					);
				},
			},
		);
	};

	useEffect(() => {
		if (validated === "yes") setSkipChoice("no");
	}, [validated]);

	const handleSaveAndClose = async () => {
		await saveValidation.mutateAsync({
			validated: validated === "yes",
			addReservesEnabled: addReservesEnabled === "yes",
			reserves: reserves.map((r) => r.text).filter((t) => t.trim().length > 0),
			onlySave: true,
		});
		window.history.back();
	};

	return (
		<div className="flex flex-col gap-6 p-6">
			<h2 className="text-2xl font-medium text-text-secondary-blue">
				Validation
			</h2>
			<Card className="py-0">
				<CardContent className="flex flex-col gap-8 p-6">
					<div className="grid grid-cols-[280px_1fr] items-center gap-6">
						<Label className="text-foreground whitespace-nowrap text-sm md:text-base font-medium">
							Valider le chantier ?
						</Label>
						<RadioGroup
							className="flex flex-row gap-8"
							value={validated}
							onValueChange={(val) => setValidated(val as "yes" | "no")}
						>
							<div className="flex items-center gap-3">
								<RadioGroupItem id="validate-yes" value="yes" />
								<Label
									htmlFor="validate-yes"
									className="text-foreground text-sm"
								>
									Oui
								</Label>
							</div>
							<div className="flex items-center gap-3">
								<RadioGroupItem id="validate-no" value="no" />
								<Label
									htmlFor="validate-no"
									className="text-foreground text-sm"
								>
									Non
								</Label>
							</div>
						</RadioGroup>
					</div>

					{validated === "no" && (
						<div className="grid grid-cols-[280px_1fr] items-center gap-6">
							<Label className="text-foreground whitespace-nowrap text-sm md:text-base font-medium">
								Classer le chantier sans suite ?
							</Label>
							<RadioGroup
								className="flex flex-row gap-8"
								value={skipChoice}
								onValueChange={(val) => setSkipChoice(val as "yes" | "no")}
							>
								<div className="flex items-center gap-3">
									<RadioGroupItem id="skip-yes" value="yes" />
									<Label htmlFor="skip-yes" className="text-foreground text-sm">
										Oui
									</Label>
								</div>
								<div className="flex items-center gap-3">
									<RadioGroupItem id="skip-no" value="no" />
									<Label htmlFor="skip-no" className="text-foreground text-sm">
										Non
									</Label>
								</div>
							</RadioGroup>
						</div>
					)}

					{validated === "yes" && (
						<>
							<div className="grid grid-cols-[280px_1fr] items-center gap-6">
								<Label className="text-foreground whitespace-nowrap text-sm md:text-base font-medium">
									Ajouter des réserves à la proposition ?
								</Label>
								<RadioGroup
									className="flex flex-row gap-8"
									value={addReservesEnabled}
									onValueChange={(val) =>
										setAddReservesEnabled(val as "yes" | "no")
									}
								>
									<div className="flex items-center gap-3">
										<RadioGroupItem id="reserves-yes" value="yes" />
										<Label
											htmlFor="reserves-yes"
											className="text-foreground text-sm"
										>
											Oui
										</Label>
									</div>
									<div className="flex items-center gap-3">
										<RadioGroupItem id="reserves-no" value="no" />
										<Label
											htmlFor="reserves-no"
											className="text-foreground text-sm"
										>
											Non
										</Label>
									</div>
								</RadioGroup>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								{addReservesEnabled === "yes" && (
									<div>
										<div className="text-base font-medium text-primary uppercase">
											Liste des réserves
										</div>
										<div className="flex flex-col gap-3">
											{reserves.map((reserve, idx) => (
												<div
													key={reserve.id}
													className="flex items-center gap-3"
												>
													<Input
														value={reserve.text}
														ref={(el) => {
															inputRefs.current[reserve.id] = el;
														}}
														onChange={(e) =>
															handleReserveChange(idx, e.target.value)
														}
														placeholder="Décrire la réserve"
													/>
													<Button
														variant="ghost"
														size="icon"
														className="h-10 w-10"
														aria-label="Supprimer la réserve"
														onClick={() => handleRemoveReserve(idx)}
													>
														<Trash2 className="h-4 w-4" />
													</Button>
												</div>
											))}
											<div>
												<Button variant="outline" onClick={handleAddReserve}>
													+ Ajouter une réserve
												</Button>
											</div>
										</div>
									</div>
								)}

								{addReservesEnabled === "yes" && (
									<div className="flex flex-col gap-6 p-6 bg-muted rounded-xl">
										<div className="text-sm text-foreground">
											<span className="whitespace-nowrap">
												La proposition est établie sous réserve
											</span>
											<span className="whitespace-nowrap">
												{" "}
												de la transmission des éléments suivants :
											</span>
										</div>
										<Separator />
										<ul className="list-disc pl-6 text-sm text-foreground space-y-1">
											{reserves.map((r) => (
												<li key={`preview-${r.id}`}>{r.text || "Réserve"}</li>
											))}
											{reserves.length === 0 && <li>—</li>}
										</ul>
									</div>
								)}
							</div>
						</>
					)}
				</CardContent>
			</Card>
			<div className="flex flex-row h-11 items-center justify-between w-full mt-4">
				<Button variant="outline" onClick={handlePreviousStep}>
					Précédent
				</Button>
				<div className="flex flex-row gap-4 items-center">
					<Button
						variant="outline"
						onClick={handleSaveAndClose}
						isLoading={saveValidation.isPending}
					>
						Enregistrer et fermer
					</Button>
					{validated === "no" && skipChoice === "yes" ? (
						<Button
							onClick={async () => {
								await toggleSkipMutation.mutateAsync({
									id: constructionSiteId,
									skipReason: "not_realized",
								});
							}}
							isLoading={toggleSkipMutation.isPending}
						>
							Classer sans suite
						</Button>
					) : (
						<Button
							onClick={handleFinalize}
							isLoading={saveValidation.isPending}
							disabled={validated === "no"}
						>
							Valider
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};
